import { FC, useMemo, useState } from "react";
import { Button, Select } from "antd";
import { Package, Calculator, Plus, Trash2, Box } from "lucide-react";
import { useGetMyServiceTicketInventories } from "~/services/service-ticket";
import { useGetInventoriesInfinite } from "~/services/inventory";
import { useGetPackingMetrics, serviceMetricsApi } from "~/services/service-metrics";
import { useBooking } from "~/store";
import { TInventory, TServiceTicketSupplyItem } from "~/types";
import { CheckType, PackingTypes } from "~/utils/constants";

interface SelectedLooseItem {
  inventoryId: string;
  packingType: PackingTypes | null;
}

interface PackingEstimatorProps {
  ticketId: string;
  inventoryIds?: string[];
  onSuppliesCalculated?: (supplies: Omit<TServiceTicketSupplyItem, "id">[]) => void;
}

export const PackingEstimator: FC<PackingEstimatorProps> = ({
  ticketId,
  inventoryIds = [],
  onSuppliesCalculated,
}) => {
  const { clientId, booking } = useBooking();
  const [selectedItems, setSelectedItems] = useState<SelectedLooseItem[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculatedSupplies, setCalculatedSupplies] = useState<{
    supplyId: string;
    supplyName: string;
    quantity: number;
    unitCost?: number;
  }[]>([]);

  // Fetch inventory data for this ticket (already saved inventories)
  const { data: ticketData } = useGetMyServiceTicketInventories(ticketId);

  // Also fetch inventories that might be newly added (not yet saved)
  const { data: inventoriesData } = useGetInventoriesInfinite({
    checkParentId: CheckType.CHECK,
    pageSize: 100,
    clientId: clientId ?? booking?.clientId ?? "",
  });

  const allInventories = useMemo(() => {
    return inventoriesData?.pages.flatMap((page) => page.items) ?? [];
  }, [inventoriesData]);

  // Fetch packing metrics
  const { data: packingMetrics } = useGetPackingMetrics();

  // Get loose inventory items from the selected inventory IDs
  const looseInventoryItems = useMemo(() => {
    if (!inventoryIds || inventoryIds.length === 0) {
      return [];
    }

    // Look up each inventory ID from either ticket data or all inventories
    const items: TInventory[] = [];
    for (const id of inventoryIds) {
      const fromTicket = ticketData?.inventories?.find((inv: TInventory) => inv.id === id);
      const fromAll = allInventories.find((inv) => inv.id === id);
      const inventory = fromTicket || fromAll;

      if (inventory && (inventory.packingType === PackingTypes.LOOSE || !inventory.packingType)) {
        items.push(inventory);
      }
    }
    return items;
  }, [ticketData?.inventories, allInventories, inventoryIds]);

  // Get available packing types that fit an inventory item's dimensions
  const getAvailablePackingTypes = (inventory: TInventory): PackingTypes[] => {
    if (!packingMetrics || !inventory.length || !inventory.width || !inventory.height) {
      return Object.values(PackingTypes);
    }

    const length = inventory.length || 0;
    const width = inventory.width || 0;
    const height = inventory.height || 0;

    const fittingMetrics = packingMetrics.filter((metric) => {
      const maxL = Number(metric.maxLength) || Infinity;
      const maxW = Number(metric.maxWidth) || Infinity;
      const maxH = Number(metric.maxHeight) || Infinity;
      return length <= maxL && width <= maxW && height <= maxH;
    });

    return [...new Set(fittingMetrics.map((m) => m.packingType as PackingTypes))];
  };

  // Get the best metric for a specific packing type and inventory dimensions
  const getBestMetricForType = (inventory: TInventory, packingType: PackingTypes) => {
    if (!packingMetrics) return null;

    const length = inventory.length || 0;
    const width = inventory.width || 0;
    const height = inventory.height || 0;

    const fittingMetrics = packingMetrics
      .filter((metric) => {
        if (metric.packingType !== packingType) return false;
        const maxL = Number(metric.maxLength) || Infinity;
        const maxW = Number(metric.maxWidth) || Infinity;
        const maxH = Number(metric.maxHeight) || Infinity;
        return length <= maxL && width <= maxW && height <= maxH;
      })
      .sort((a, b) => {
        const aVolume =
          (Number(a.maxLength) || 999) *
          (Number(a.maxWidth) || 999) *
          (Number(a.maxHeight) || 999);
        const bVolume =
          (Number(b.maxLength) || 999) *
          (Number(b.maxWidth) || 999) *
          (Number(b.maxHeight) || 999);
        return aVolume - bVolume;
      });

    return fittingMetrics[0] || null;
  };

  // Calculate material cost
  const calculateMaterialCost = (
    length: number,
    width: number,
    height: number,
    packingType: PackingTypes | null
  ): number => {
    if (!packingType || !packingMetrics || !length || !width || !height) {
      return 0;
    }

    const metric = packingMetrics.find((m) => m.packingType === packingType);
    if (!metric) return 0;

    const materialRate = Number((metric as any).materialRatePerSqFt) || 0;
    const baseRate = Number(metric.baseRate) || 0;
    const ratePerSqFt = materialRate || baseRate;

    const bufferedL = length * 1.05;
    const bufferedW = width * 1.05;
    const bufferedH = height * 1.05;
    const surfaceAreaSqFt =
      (2 * (bufferedL * bufferedW + bufferedL * bufferedH + bufferedW * bufferedH)) / 144;

    return surfaceAreaSqFt * ratePerSqFt;
  };

  // Add a loose item to the selection
  const addItem = (inventoryId: string) => {
    if (!selectedItems.find((item) => item.inventoryId === inventoryId)) {
      setSelectedItems([...selectedItems, { inventoryId, packingType: null }]);
    }
  };

  // Remove an item from selection
  const removeItem = (inventoryId: string) => {
    setSelectedItems(selectedItems.filter((item) => item.inventoryId !== inventoryId));
  };

  // Update packing type for an item
  const updatePackingType = (inventoryId: string, packingType: PackingTypes | null) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.inventoryId === inventoryId ? { ...item, packingType } : item
      )
    );
  };

  // Helper to find inventory by ID
  const findInventory = (inventoryId: string): TInventory | undefined => {
    return looseInventoryItems.find((inv) => inv.id === inventoryId);
  };

  // Calculate totals
  const totals = useMemo(() => {
    let totalMaterialCost = 0;
    let totalPackingMinutes = 0;

    selectedItems.forEach((item) => {
      if (!item.packingType) return;

      const inventory = findInventory(item.inventoryId);
      if (!inventory) return;

      const bestMetric = getBestMetricForType(inventory, item.packingType);
      if (bestMetric) {
        totalMaterialCost += calculateMaterialCost(
          inventory.length || 0,
          inventory.width || 0,
          inventory.height || 0,
          item.packingType
        );
        totalPackingMinutes += Number(bestMetric.estimatedMinutesPerItem) || 0;
      }
    });

    return {
      materialCost: Math.round(totalMaterialCost * 100) / 100,
      packingMinutes: totalPackingMinutes,
      packingHours: (totalPackingMinutes / 60).toFixed(1),
    };
  }, [selectedItems, looseInventoryItems, packingMetrics]);

  // Calculate supplies for all selected items
  const calculateSupplies = async () => {
    setIsCalculating(true);
    const suppliesMap = new Map<
      string,
      { supplyId: string; supplyName: string; quantity: number; unitCost?: number }
    >();

    try {
      for (const item of selectedItems) {
        if (!item.packingType) continue;

        const inventory = findInventory(item.inventoryId);
        if (!inventory) continue;

        const bestMetric = getBestMetricForType(inventory, item.packingType);
        if (!bestMetric) continue;

        const response = await serviceMetricsApi.getMaterialBreakdown(
          bestMetric.id,
          inventory.length || 0,
          inventory.width || 0,
          inventory.height || 0
        );
        const breakdown = response?.data;

        if (breakdown?.materials) {
          for (const material of breakdown.materials) {
            if (suppliesMap.has(material.supplyId)) {
              const existing = suppliesMap.get(material.supplyId)!;
              existing.quantity += material.quantityNeeded;
            } else {
              suppliesMap.set(material.supplyId, {
                supplyId: material.supplyId,
                supplyName: material.supplyName,
                quantity: material.quantityNeeded,
                unitCost: material.billableRatePerUnit,
              });
            }
          }
        }
      }

      const roundToQuarter = (n: number) => Math.max(1, Math.ceil(n * 4) / 4);
      const supplies = Array.from(suppliesMap.values()).map((s) => ({
        ...s,
        quantity: roundToQuarter(s.quantity),
      }));

      setCalculatedSupplies(supplies);

      // Notify parent with calculated supplies
      if (onSuppliesCalculated) {
        onSuppliesCalculated(
          supplies.map((s) => ({
            supplyId: s.supplyId,
            quantity: s.quantity,
            unitCost: s.unitCost,
          }))
        );
      }

      return supplies;
    } catch (err) {
      console.error("Failed to calculate supplies:", err);
      return [];
    } finally {
      setIsCalculating(false);
    }
  };

  // Don't render if no loose items
  if (looseInventoryItems.length === 0) {
    return (
      <div className="text-sm text-gray-400 text-center py-4 border-2 border-dashed border-gray-200 rounded">
        <Package size={20} className="mx-auto mb-2 opacity-50" />
        No loose items to pack
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with totals */}
      {selectedItems.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-gray-600 pb-2 border-b">
          <span>
            <strong className="text-gray-900">{selectedItems.length}</strong> item
            {selectedItems.length !== 1 ? "s" : ""} selected
          </span>
          {totals.materialCost > 0 && (
            <span>
              <strong className="text-gray-900">${totals.materialCost.toFixed(2)}</strong> materials
            </span>
          )}
          {totals.packingMinutes > 0 && (
            <span>
              <strong className="text-gray-900">{totals.packingHours}</strong> hrs packing
            </span>
          )}
        </div>
      )}

      {/* Unselected loose items */}
      {looseInventoryItems.filter(
        (inv) => !selectedItems.find((s) => s.inventoryId === inv.id)
      ).length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-gray-500 font-medium">Available Loose Items:</div>
          <div className="flex flex-wrap gap-1">
            {looseInventoryItems
              .filter((inv) => !selectedItems.find((s) => s.inventoryId === inv.id))
              .map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => addItem(inv.id)}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-blue-100 rounded flex items-center gap-1"
                >
                  <Plus size={12} />
                  {inv.title || inv.inventoryRef}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Selected items with packing type selection */}
      {selectedItems.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-gray-500 font-medium">Pack These Items:</div>
          {selectedItems.map((item) => {
            const inventory = looseInventoryItems.find((inv: TInventory) => inv.id === item.inventoryId);
            if (!inventory) return null;

            const availableTypes = getAvailablePackingTypes(inventory);
            const dims = `${inventory.length || "?"}"×${inventory.width || "?"}"×${inventory.height || "?"}"`;

            return (
              <div
                key={item.inventoryId}
                className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded"
              >
                <Box size={14} className="text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">
                    {inventory.title || inventory.inventoryRef}
                  </div>
                  <div className="text-xs text-gray-500">{dims}</div>
                </div>
                <Select
                  size="small"
                  placeholder="Packing type"
                  value={item.packingType}
                  onChange={(val) => updatePackingType(item.inventoryId, val)}
                  className="w-32"
                  options={availableTypes.map((type) => ({
                    label: type.replace(/_/g, " "),
                    value: type,
                  }))}
                />
                <button
                  type="button"
                  onClick={() => removeItem(item.inventoryId)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Calculate button */}
      {selectedItems.some((item) => item.packingType) && (
        <Button
          size="small"
          type="primary"
          ghost
          loading={isCalculating}
          onClick={calculateSupplies}
          className="w-full"
          icon={<Calculator size={14} />}
        >
          Calculate Supplies
        </Button>
      )}

      {/* Calculated supplies display */}
      {calculatedSupplies.length > 0 && (
        <div className="p-2 bg-green-50 border border-green-200 rounded text-xs">
          <div className="font-medium text-green-800 mb-1">
            Calculated Supplies ({calculatedSupplies.length})
          </div>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {calculatedSupplies.map((supply, idx) => (
              <div key={idx} className="flex justify-between text-green-700">
                <span>{supply.supplyName}</span>
                <span>
                  {supply.quantity} × ${(supply.unitCost || 0).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
